// ABOUTME: Email audit log tab for admin email campaign management
// ABOUTME: Displays detailed audit trail of all email-related events with filtering and CSV export
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Stack,
} from '@mui/material';
import { Download } from '@mui/icons-material';
import axios from 'axios';
import { formatAbsoluteDateTime, formatRelativeTime } from '../../utils/timeUtils';
import { useTimezone } from '../../contexts/TimezoneContext';
import { getCurrentAccessToken } from '../../services/api';

interface EmailAuditLogDto {
  id: number;
  timestampUTC: string;
  timestampInFamilyTimezone: string;
  eventType: string;
  campaignName: string;
  familyName: string;
  familyTimezone: string;
  familyId: number;
  childName?: string;
  childId?: number;
  recipientEmail: string;
  status: string;
  errorMessage?: string;
  details: string;
}

interface PagedResponse {
  data: EmailAuditLogDto[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

interface FamilyOption {
  id: number;
  name: string;
}

interface CampaignOption {
  id: number;
  name: string;
}

const EVENT_TYPES = ['Sent', 'Failed', 'Bounced', 'Delivered', 'Opened', 'Clicked'];

export default function EmailAuditLogTab() {
  const { getTodayInFamilyTimezone } = useTimezone();
  const [auditLogs, setAuditLogs] = useState<EmailAuditLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  // Filter states
  const [familyId, setFamilyId] = useState<number | ''>('');
  const [campaignId, setCampaignId] = useState<number | ''>('');
  const [eventType, setEventType] = useState<string>('');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [search, setSearch] = useState<string>('');

  // Dropdown options
  const [families, setFamilies] = useState<FamilyOption[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignOption[]>([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchAuditLogs is a plain function in component scope; the effect correctly re-runs when the listed deps change, not when the function reference changes
  }, [page, pageSize, familyId, campaignId, eventType, fromDate, toDate, search]);

  const fetchFilterOptions = async () => {
    try {
      const token = getCurrentAccessToken();
      const headers = { Authorization: `Bearer ${token}` };
      const baseUrl = import.meta.env.VITE_API_URL;

      // Fetch families
      const familiesResponse = await axios.get<{ data: FamilyOption[] }>(
        `${baseUrl}/api/admin/families`,
        {
          params: { page: 1, pageSize: 1000, paginate: false },
          headers,
        }
      );
      setFamilies(familiesResponse.data.data || []);

      // Fetch campaigns
      const campaignsResponse = await axios.get<{
        data: CampaignOption[];
        page: number;
        pageSize: number;
        totalCount: number;
        totalPages: number;
      }>(
        `${baseUrl}/api/admin/email-campaigns`,
        {
          params: { page: 1, pageSize: 100 },
          headers,
        }
      );
      setCampaigns((campaignsResponse.data.data || []).map(c => ({ id: c.id, name: c.name })));
    } catch (err: unknown) {
      console.error('Error fetching filter options:', err);
    }
  };

  const fetchAuditLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getCurrentAccessToken();
      const params: {
        page: number;
        pageSize: number;
        familyId?: number;
        campaignId?: number;
        eventType?: string;
        fromDate?: string;
        toDate?: string;
        search?: string;
      } = {
        page: page + 1, // Backend uses 1-based pagination
        pageSize: pageSize,
      };

      if (familyId) params.familyId = familyId;
      if (campaignId) params.campaignId = campaignId;
      if (eventType) params.eventType = eventType;
      if (fromDate) params.fromDate = new Date(fromDate).toISOString();
      if (toDate) params.toDate = new Date(toDate).toISOString();
      if (search) params.search = search;

      const response = await axios.get<PagedResponse>(
        `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/admin/email-campaigns/audit-log`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAuditLogs(response.data.data);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
      console.error('Error fetching audit logs:', err);
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      setError(message || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getEventTypeColor = (eventType: string): 'default' | 'success' | 'error' | 'warning' | 'info' => {
    switch (eventType.toLowerCase()) {
      case 'sent':
      case 'delivered':
        return 'success';
      case 'failed':
      case 'bounced':
        return 'error';
      case 'opened':
      case 'clicked':
        return 'info';
      default:
        return 'default';
    }
  };

  const clearFilters = () => {
    setFamilyId('');
    setCampaignId('');
    setEventType('');
    setFromDate('');
    setToDate('');
    setSearch('');
  };

  const exportToCsv = () => {
    if (auditLogs.length === 0) {
      return;
    }

    // CSV headers
    const headers = [
      'Timestamp (Family Timezone)',
      'Event Type',
      'Status',
      'Campaign',
      'Family',
      'Child Name',
      'Recipient Email',
      'Error Message',
      'Details',
    ];

    // CSV rows
    const rows = auditLogs.map(log => [
      formatAbsoluteDateTime(log.timestampInFamilyTimezone),
      log.eventType,
      log.status,
      log.campaignName,
      log.familyName,
      log.childName || '',
      log.recipientEmail,
      log.errorMessage || '',
      log.details,
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row =>
        row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
      ),
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `email-audit-log-${getTodayInFamilyTimezone()}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && auditLogs.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Filters
          </Typography>
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={exportToCsv}
            disabled={auditLogs.length === 0}
          >
            Export to CSV
          </Button>
        </Box>
        <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Family</InputLabel>
              <Select
                value={familyId}
                label="Family"
                onChange={(e) => setFamilyId(e.target.value as number | '')}
              >
                <MenuItem value="">All Families</MenuItem>
                {families.map((family) => (
                  <MenuItem key={family.id} value={family.id}>
                    {family.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Campaign</InputLabel>
              <Select
                value={campaignId}
                label="Campaign"
                onChange={(e) => setCampaignId(e.target.value as number | '')}
              >
                <MenuItem value="">All Campaigns</MenuItem>
                {campaigns.map((campaign) => (
                  <MenuItem key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Event Type</InputLabel>
              <Select
                value={eventType}
                label="Event Type"
                onChange={(e) => setEventType(e.target.value)}
              >
                <MenuItem value="">All Events</MenuItem>
                {EVENT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box sx={{ minWidth: 200, flexGrow: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              placeholder="Email or details"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <Button variant="outlined" onClick={clearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table aria-label="email audit log table">
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Event Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Campaign</TableCell>
              <TableCell>Family</TableCell>
              <TableCell>Child</TableCell>
              <TableCell>Recipient</TableCell>
              <TableCell>Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {auditLogs.map((log) => (
              <TableRow key={log.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {formatAbsoluteDateTime(log.timestampInFamilyTimezone)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatRelativeTime(log.timestampUTC, log.familyTimezone)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={log.eventType}
                    size="small"
                    color={getEventTypeColor(log.eventType)}
                  />
                </TableCell>
                <TableCell>{log.status}</TableCell>
                <TableCell>{log.campaignName}</TableCell>
                <TableCell>
                  <Tooltip title={`Timezone: ${log.familyTimezone}`}>
                    <span>{log.familyName}</span>
                  </Tooltip>
                </TableCell>
                <TableCell>{log.childName || '-'}</TableCell>
                <TableCell>{log.recipientEmail}</TableCell>
                <TableCell>
                  {log.errorMessage ? (
                    <Tooltip title={log.errorMessage}>
                      <Typography variant="body2" color="error" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        Error: {log.errorMessage}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Tooltip title={log.details}>
                      <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {log.details}
                      </Typography>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
